import {
  QButton,
  QCloseButton,
  QLookup,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QSelect,
  QSelectItem,
  QText,
  useToastProvider,
} from '@qualio/ui-components';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addUsersToGroup } from '../../api/medtech-api';
import { useGroupLookupView } from '../../hooks/useGroupLookupView';
import { Group, ModalComponentProps, User } from '../../types';
import { formatGroupsToSelectOptions } from '../../utils/groups.utils';
import { useFlagProvider } from '../../utils/flagProvider';
import { createErrorToast, createSuccessToast } from '../../utils/toast.utils';

type AddToGroupModalProps = ModalComponentProps & {
  users: User[];
  groups: Group[];
  getUsers: () => Promise<void>;
};

const AddToGroupModalLegacy = ({ closeModal, users, groups, getUsers }: AddToGroupModalProps) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const [selectedGroup, setSelectedGroup] = useState<QSelectItem>();
  const [isLoading, setLoading] = useState(false);

  const options = useMemo(() => formatGroupsToSelectOptions(groups), [groups]);

  const handleSubmit = async () => {
    if (!selectedGroup) return;
    setLoading(true);
    try {
      await addUsersToGroup({ groups: [+selectedGroup.value], users: users.map((u) => u.id) });
      await getUsers();
      showToast(
        createSuccessToast(t('messages.success.usersAdded', { count: users.length }), 'group-add-users-success-toast'),
      );
    } catch (err: any) {
      showToast(
        createErrorToast(
          err.response?.data?.message || t('messages.error.addUsersToGroup'),
          'group-add-users-error-toast',
        ),
      );
    } finally {
      setLoading(false);
      closeModal();
    }
  };

  return (
    <QModal onClose={closeModal} size="md" isOpen>
      <QModalHeader>
        <QText data-cy="heading">{t('modals.addToGroup.title')}</QText>
        <QCloseButton onClick={closeModal} />
      </QModalHeader>
      <QModalBody>
        <QSelect
          aria-label="group"
          value={selectedGroup?.label}
          onChange={(e: any) => setSelectedGroup(e)}
          options={options}
        />
      </QModalBody>
      <QModalActions>
        <QButton data-cy="cancel-button" variant="outline" onClick={closeModal}>
          {t('common.cancel')}
        </QButton>
        <QButton data-cy="add-button" isDisabled={!selectedGroup} isLoading={isLoading} onClick={handleSubmit}>
          {t('common.add')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};

const AddToGroupModalQLookup = ({ closeModal, users, groups, getUsers }: AddToGroupModalProps) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const [isLoading, setLoading] = useState(false);

  const { groupDataView, filterOption } = useGroupLookupView();

  const onSelect = useCallback(
    async (selectedGroups: readonly Group[]) => {
      if (isLoading) return;
      setLoading(true);
      try {
        await addUsersToGroup({
          groups: selectedGroups.map((g) => g.id),
          users: users.map((u) => u.id),
        });
        await getUsers();
        showToast(
          createSuccessToast(
            t('messages.success.usersAdded', { count: users.length }),
            'group-add-users-success-toast',
          ),
        );
        closeModal();
      } catch (err: any) {
        showToast(
          createErrorToast(
            err.response?.data?.message || t('messages.error.addUsersToGroup'),
            'group-add-users-error-toast',
          ),
        );
        closeModal();
      } finally {
        setLoading(false);
      }
    },
    [isLoading, users, getUsers, closeModal, showToast, t],
  );

  return (
    <QLookup.DataProvider.Fixed data={groups} filterOption={filterOption}>
      <QLookup.MultiSelect<Group>
        isOpen
        action={isLoading ? t('modals.addToGroup.adding') : t('common.add')}
        searchPlaceholder={t('modals.manageGroups.searchPlaceholder')}
        accessors={{ id: 'id' }}
        isItemDisabled={() => false}
        isItemPreSelected={() => false}
        onSelect={onSelect}
        onCancel={closeModal}
        view={groupDataView}
        defaultSortBy={{ id: 'name' }}
      >
        <QModalHeader>
          <QText>{t('modals.addToGroup.title')}</QText>
        </QModalHeader>
      </QLookup.MultiSelect>
    </QLookup.DataProvider.Fixed>
  );
};

export const AddToGroupModal = (props: AddToGroupModalProps) => {
  const { isFlagEnabled } = useFlagProvider();
  return isFlagEnabled('multiGroupAssignment') ? (
    <AddToGroupModalQLookup {...props} />
  ) : (
    <AddToGroupModalLegacy {...props} />
  );
};
